import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATUSES, STATUS_LABEL, STATUS_STYLES, APPLIANCE_TYPES, JOB_TYPES, findLabel, formatDateTime } from "@/lib/options";
import { Plus, Search, X } from "lucide-react";
import { toast } from "sonner";

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [appType, setAppType] = useState("all");
  const [jobType, setJobType] = useState("all");
  const [loading, setLoading] = useState(false);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (status !== "all") params.status = status;
      if (appType !== "all") params.appliance_type = appType;
      if (jobType !== "all") params.job_type = jobType;
      const { data } = await api.get("/tickets", { params });
      setTickets(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [search, status, appType, jobType]);

  useEffect(() => {
    fetchTickets();
    // `search` is intentionally excluded — typing should not trigger requests;
    // only the Search button (via onSearch) does. Filter selects auto-fetch.
  }, [status, appType, jobType]);

  const onSearch = (e) => {
    e.preventDefault();
    fetchTickets();
  };

  const clearFilters = () => {
    setSearch("");
    setStatus("all");
    setAppType("all");
    setJobType("all");
  };

  const hasFilters = useMemo(
    () => search || status !== "all" || appType !== "all" || jobType !== "all",
    [search, status, appType, jobType]
  );

  return (
    <div className="space-y-6" data-testid="tickets-root">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Tickets</h1>
          <p className="text-sm text-slate-500 mt-1">Track every repair and installation job.</p>
        </div>
        <Link to="/tickets/new">
          <Button className="bg-blue-700 hover:bg-blue-800 gap-2" data-testid="new-ticket-btn">
            <Plus className="w-4 h-4" /> New Ticket
          </Button>
        </Link>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-3">
          <form onSubmit={onSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search by customer, phone, city, model, serial…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="ticket-search-input"
              />
            </div>
            <Button type="submit" variant="outline" data-testid="ticket-search-btn">Search</Button>
            {hasFilters && (
              <Button type="button" variant="ghost" onClick={clearFilters} data-testid="clear-filters" className="gap-1">
                <X className="w-4 h-4" /> Clear
              </Button>
            )}
          </form>

          <div className="flex flex-wrap gap-2">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-[160px]" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={appType} onValueChange={setAppType}>
              <SelectTrigger className="w-[180px]" data-testid="filter-appliance">
                <SelectValue placeholder="Appliance" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Appliances</SelectItem>
                {APPLIANCE_TYPES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={jobType} onValueChange={setJobType}>
              <SelectTrigger className="w-[160px]" data-testid="filter-jobtype">
                <SelectValue placeholder="Job Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Job Types</SelectItem>
                {JOB_TYPES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Dispatch ID</TableHead>
                <TableHead>Appliance</TableHead>
                <TableHead>Job</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Scheduled</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">Loading…</TableCell></TableRow>
              )}
              {!loading && tickets.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-500 py-10" data-testid="empty-tickets">
                    No tickets found. <Link to="/tickets/new" className="text-blue-700 font-medium">Create one</Link>.
                  </TableCell>
                </TableRow>
              )}
              {!loading && tickets.map((t) => (
                <TableRow key={t.id} className="hover:bg-slate-50" data-testid={`ticket-row-${t.id}`}>
                  <TableCell>
                    <div className="font-medium text-slate-900">
                      {t.customer ? `${t.customer.first_name} ${t.customer.last_name}` : "—"}
                    </div>
                    <div className="text-xs text-slate-500">{t.customer?.phone}</div>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-700">
                    {t.dispatch_id || <span className="text-slate-400 font-sans">—</span>}
                  </TableCell>
                  <TableCell>
                    <div className="capitalize text-slate-900">{findLabel(APPLIANCE_TYPES, t.appliance_type)}</div>
                    <div className="text-xs text-slate-500 capitalize">{t.manufacturer}</div>
                  </TableCell>
                  <TableCell className="capitalize">{findLabel(JOB_TYPES, t.job_type)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`${STATUS_STYLES[t.status]} rounded-full`}>
                      {STATUS_LABEL[t.status]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{formatDateTime(t.scheduled_at)}</TableCell>
                  <TableCell className="text-right">
                    <Link to={`/tickets/${t.id}`}>
                      <Button size="sm" variant="outline" data-testid={`view-ticket-${t.id}`}>View</Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
